import { useEffect, useState } from "react";
import { subscribeInflight } from "@/lib/api";

/**
 * Thin top progress bar that slides across while any axios request is in flight.
 * 2px tall, teal→orange gradient. Fades out when all requests settle.
 */
export default function GlobalProgressBar() {
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const unsub = subscribeInflight((n) => setCount(n));
    return unsub;
  }, []);

  useEffect(() => {
    let raf = null;
    let fadeTimer = null;
    if (count > 0) {
      setVisible(true);
      // Simulated progress: creep towards 90% while requests pending
      let p = 10;
      setProgress(10);
      const tick = () => {
        p = Math.min(90, p + (90 - p) * 0.05);
        setProgress(p);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    } else if (visible) {
      // Snap to 100%, then fade out after 250ms
      setProgress(100);
      fadeTimer = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 350);
    }
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] pointer-events-none"
      data-testid="global-progress-bar"
      aria-hidden="true"
    >
      <div
        className="h-[2px] origin-left transition-opacity duration-300"
        style={{
          background: "linear-gradient(90deg, #14b8a6 0%, #F26A1F 100%)",
          transform: `scaleX(${progress / 100})`,
          opacity: visible ? 1 : 0,
          transition: "transform 180ms ease-out, opacity 300ms ease-out",
        }}
        data-testid="global-progress-fill"
      />
    </div>
  );
}
